"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import type { CookiePreferences } from "@/components/cookie-banner";

interface CookieSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (preferences: CookiePreferences) => void;
}

interface CookieCategory {
  id: keyof CookiePreferences;
  title: string;
  description: string;
  required?: boolean;
  examples: string[];
}

const COOKIE_CATEGORIES: CookieCategory[] = [
  {
    id: "necessary",
    title: "Strictly Necessary Cookies",
    description: "These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt-out of these cookies.",
    required: true,
    examples: [
      "Session authentication",
      "Security tokens",
      "Load balancing",
      "CSRF protection"
    ],
  },
  {
    id: "functional",
    title: "Functional Cookies",
    description: "These cookies enable enhanced functionality and personalization, such as remembering your preferences (e.g., language, region) and providing personalized features.",
    examples: [
      "Language preferences",
      "Theme selection (dark/light mode)",
      "Recently viewed items",
      "User interface customizations"
    ],
  },
  {
    id: "analytics",
    title: "Performance & Analytics Cookies",
    description: "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website's performance.",
    examples: [
      "Google Analytics",
      "Page view tracking",
      "User behavior analysis",
      "Performance monitoring"
    ],
  },
  {
    id: "marketing",
    title: "Marketing & Targeting Cookies",
    description: "These cookies track your online activity to help advertisers deliver more relevant advertising or to limit how many times you see an ad. They may be set by advertising partners.",
    examples: [
      "Advertising pixels (Meta, LinkedIn)",
      "Retargeting campaigns",
      "Conversion tracking",
      "Social media integration"
    ],
  },
];

// Toggle Switch Component
function ToggleSwitch({
  enabled,
  onChange,
  disabled = false
}: {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => !disabled && onChange(!enabled)}
      className={`
        relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2
        focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background
        ${enabled ? 'bg-accent' : 'bg-foreground/20'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow-lg
          ring-0 transition duration-200 ease-in-out
          ${enabled ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

// Cookie Category Card
function CategoryCard({
  category,
  enabled,
  onChange
}: {
  category: CookieCategory;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-border/40 rounded-lg overflow-hidden bg-foreground/[0.02] hover:border-border/60 transition-colors">
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-foreground text-[15px] sm:text-base">
                {category.title}
              </h4>
              {category.required && (
                <span className="text-[11px] sm:text-xs px-2 py-0.5 rounded-full bg-accent/10 text-accent font-medium shrink-0">
                  Required
                </span>
              )}
            </div>
            <p className="text-[13px] sm:text-sm text-foreground-secondary leading-relaxed mb-3">
              {category.description}
            </p>

            {/* Expandable Examples */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center gap-1 text-xs text-accent hover:text-accent/80 transition-colors font-medium"
            >
              <Info className="w-3.5 h-3.5" />
              {expanded ? "Hide" : "Show"} examples
            </button>

            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <ul className="mt-3 space-y-1.5 text-xs text-foreground-secondary pl-4">
                    {category.examples.map((example, idx) => (
                      <li key={idx} className="list-disc">
                        {example}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Toggle Switch */}
          <div className="shrink-0 pt-1">
            <ToggleSwitch
              enabled={enabled}
              onChange={onChange}
              disabled={category.required}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function CookieSettingsModal({ isOpen, onClose, onSave }: CookieSettingsModalProps) {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    functional: false,
    marketing: false,
  });

  // Load existing preferences when modal opens
  useEffect(() => {
    if (isOpen) {
      const saved = localStorage.getItem("cookie-preferences");
      if (saved) {
        try {
          setPreferences(JSON.parse(saved));
        } catch {
          // Invalid saved data, use defaults
        }
      }
    }
  }, [isOpen]);

  const handleToggle = (category: keyof CookiePreferences, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [category]: value,
    }));
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true,
    };
    onSave(allAccepted);
  };

  const handleRejectAll = () => {
    const allRejected: CookiePreferences = {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false,
    };
    onSave(allRejected);
  };

  const handleSaveCustom = () => {
    onSave(preferences);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70]"
            onClick={onClose}
          />

          {/* Modal */}
          <div
            className="fixed inset-0 z-[71] overflow-y-auto"
            style={{ WebkitFontSmoothing: 'antialiased', textRendering: 'optimizeLegibility' }}
          >
            <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="relative w-full max-w-3xl bg-background border border-border/40 rounded-2xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-transparent opacity-50 pointer-events-none" />

                {/* Header */}
                <div className="relative border-b border-border/40 p-5 sm:p-6">
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 sm:top-5 sm:right-5 text-foreground-secondary hover:text-foreground transition-colors p-1 rounded-lg hover:bg-foreground/5"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  <h2 className="text-[19px] sm:text-2xl font-bold text-foreground mb-2 pr-8">
                    Cookie Settings
                  </h2>
                  <p className="text-[14px] sm:text-sm text-foreground-secondary">
                    Manage your cookie preferences. You can enable or disable different types of cookies below.
                    Learn more in our{" "}
                    <Link href="/cookies" className="text-accent hover:underline">
                      Cookie Policy
                    </Link>.
                  </p>
                </div>

                {/* Content - Scrollable */}
                <div className="relative max-h-[60vh] overflow-y-auto p-5 sm:p-6">
                  <div className="space-y-4">
                    {COOKIE_CATEGORIES.map((category) => (
                      <CategoryCard
                        key={category.id}
                        category={category}
                        enabled={preferences[category.id]}
                        onChange={(value) => handleToggle(category.id, value)}
                      />
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="relative border-t border-border/40 p-5 sm:p-6 bg-background/50">
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <Button
                      onClick={handleSaveCustom}
                      className="flex-1 shrink-0 min-h-[56px] sm:min-h-[52px] py-4 text-[16px] sm:text-[15px] bg-accent hover:bg-accent/90 text-background font-semibold shadow-lg shadow-accent/20 hover:shadow-accent/30 transition-all px-5"
                    >
                      Save Preferences
                    </Button>
                    <Button
                      onClick={handleAcceptAll}
                      variant="outline"
                      className="flex-1 shrink-0 min-h-[56px] sm:min-h-[52px] py-4 text-[16px] sm:text-[15px] border-border hover:bg-foreground/5 hover:border-foreground/30 font-medium px-4"
                    >
                      Accept All
                    </Button>
                    <Button
                      onClick={handleRejectAll}
                      variant="ghost"
                      className="flex-1 shrink-0 min-h-[56px] sm:min-h-[52px] py-4 text-[16px] sm:text-[15px] text-foreground-secondary hover:text-foreground hover:bg-foreground/5 font-medium px-4"
                    >
                      Reject All
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
